<template>
  <base-material-card
    icon="mdi-clipboard-text-outline"
    title="Audit Log"
    class="px-5 py-3"
    style="position: relative"
  >
    <v-row style="padding: 20px" align="center">
      <v-text-field
        v-model="searchText"
        prepend-inner-icon="mdi-magnify"
        label="Search by action, model, actor or record"
        clearable
        dense
        outlined
        style="max-width: 400px"
        class="mr-4"
      />
      <datepicker-input
        label="Date Range"
        :range="true"
        :value="dateRange"
        format="MMM DD, YYYY"
        @input="onDateRangeInput"
        class="mr-2"
      />
    </v-row>

    <v-data-table
      :headers="headers"
      :items="logs"
      :loading="loading"
      item-value="id"
      :footer-props="{ itemsPerPageOptions: [10, 25, 50, 100] }"
    >
      <template
        v-slot:item.action="{ item }"
      >
        <v-chip
          :color="getActionColor(item.action)"
          small
          dark
          outlined
        >
          {{ item.action }}
        </v-chip>
      </template>

      <template
        v-slot:item.actorEmail="{ item }"
      >
        {{ item.actorEmail || 'System' }}
      </template>

      <template
        v-slot:item.recordId="{ item }"
      >
        {{ item.recordId || '—' }}
      </template>

      <template
        v-slot:item.changedFields="{ item }"
      >
        <span v-if="item.changedFields">
          <v-btn
            x-small
            text
            color="primary"
            @click="showDetail(item)"
          >
            View ({{ Object.keys(item.changedFields).length }})
          </v-btn>
        </span>
        <span v-else class="grey--text text--lighten-1">—</span>
      </template>

      <template
        v-slot:item.createdAt="{ item }"
      >
        {{ formatDate(item.createdAt) }}
      </template>

      <template
        v-slot:item.action="{ item }"
      >
        <v-tooltip bottom>
          <template #activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              x-small
              color="primary"
              class="mr-2"
              v-on="on"
              @click="showDetail(item)"
            >
              mdi-eye
            </v-icon>
          </template>
          <span>View Details</span>
        </v-tooltip>
      </template>
    </v-data-table>

    <v-dialog
      v-model="detailDialog"
      max-width="900"
      scrollable
      dark
    >
      <v-card dark>
        <v-card-title class="headline grey darken-3">
          Audit Log Details
          <v-spacer />
          <v-btn icon dark @click="detailDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="max-height: 70vh" class="pt-4">
          <v-row>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">Action</div>
              <v-chip :color="getActionColor(selectedLog.action)" small dark outlined>
                {{ selectedLog.action }}
              </v-chip>
            </v-col>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">Model</div>
              <div>{{ selectedLog.modelName }}</div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">Record ID</div>
              <div>{{ selectedLog.recordId || '—' }}</div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">Actor</div>
              <div>{{ selectedLog.actorEmail || 'System' }}</div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">Created At</div>
              <div>{{ formatDate(selectedLog.createdAt) }}</div>
            </v-col>
            <v-col cols="6" md="3">
              <div class="grey--text text--lighten-1 text-caption">ID</div>
              <div>{{ selectedLog.id }}</div>
            </v-col>
          </v-row>

          <v-divider class="my-4" />

          <div v-if="selectedLog.meta">
            <div class="text-subtitle-2 font-weight-medium mb-2 grey--text text--lighten-1">Meta</div>
            <pre class="json-preview">{{ formatJson(selectedLog.meta) }}</pre>
          </div>

          <div v-if="selectedLog.previousValues">
            <div class="text-subtitle-2 font-weight-medium mt-4 mb-2 grey--text text--lighten-1">Previous Values</div>
            <pre class="json-preview">{{ formatJson(selectedLog.previousValues) }}</pre>
          </div>

          <div v-if="selectedLog.currentValues">
            <div class="text-subtitle-2 font-weight-medium mt-4 mb-2 grey--text text--lighten-1">Current Values</div>
            <pre class="json-preview">{{ formatJson(selectedLog.currentValues) }}</pre>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn text @click="detailDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </base-material-card>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'
import { Q_GET_AUDIT_LOGS } from '@/gql/auditLog'
import DatepickerInput from '@/components/base/DatepickerInput'

export default {
  name: 'AuditLog',
  components: { DatepickerInput },
  data: () => ({
    loading: false,
    searchText: '',
    dateRange: [moment().startOf('week').format('YYYY-MM-DD'), moment().endOf('week').format('YYYY-MM-DD')],
    options: {},
    detailDialog: false,
    selectedLog: {},
    headers: [

      { text: 'Model', value: 'modelName', width: '140px' },
      { text: 'Record ID', value: 'recordId', width: '100px' },
      { text: 'Actor', value: 'actorEmail',  width: '140px' },
      { text: 'Timestamp', value: 'createdAt', width: '170px' },
      { text: 'Changed Fields', value: 'changedFields', width: '130px', sortable: false },
      { text: 'Actions', value: 'action', sortable: false, width: '80px', align: 'center' },
    ],
    logs: [],
  }),
  computed: {
    ...mapGetters(['roles']),
    where() {
      const where = {}
      if (this.searchText) {
        const s = this.searchText.replace(/[%_]/g, '\\$&')
        where.or = [
          { action: { like: `%${s}%` } },
          { modelName: { like: `%${s}%` } },
          { actorEmail: { like: `%${s}%` } },
          { recordId: { like: `%${s}%` } },
        ]
      }
      if (this.dateRange && Array.isArray(this.dateRange) && this.dateRange.length === 2) {
        const [start, end] = this.dateRange
        if (start) where.createdAt = { ...(where.createdAt || {}), gte: `${start}T00:00:00.000Z` }
        if (end) where.createdAt = { ...(where.createdAt || {}), lte: `${end}T23:59:59.999Z` }
      }
      return where
    },
  },
  watch: {
    searchText() {
      this._fetchLogs()
    },
    dateRange() {
      this._fetchLogs()
    },
  },
  mounted() {
    this._fetchLogs()
  },
  methods: {
    _fetchLogs() {
      this.loading = true
      this.$apollo.query({
        query: Q_GET_AUDIT_LOGS,
        variables: {
          where: this.where,
          limit: 1000,
          offset: 0,
          order: 'DESC createdAt',
        },
      }).then(({ data }) => {
        this.logs = data?.auditLogs || []
      }).catch((error) => {
        console.error('Failed to fetch audit logs:', error)
        this.$toast?.error('Failed to fetch audit logs')
      }).finally(() => {
        this.loading = false
      })
    },
    showDetail(item) {
      this.selectedLog = item
      this.detailDialog = true
    },
    formatDate(value) {
      if (!value) return '—'
      return new Date(value).toLocaleString()
    },
    formatJson(value) {
      return JSON.stringify(value, null, 2)
    },
    getActionColor(action) {
      const colors = {
        create: 'success',
        update: 'info',
        delete: 'error',
        softDelete: 'warning',
        restore: 'primary',
        login: 'primary',
        logout: 'grey',
      }
      return colors[action] || 'grey'
    },
    onDateRangeInput(vals) {
      this.dateRange = vals
    },
  },
}
</script>

<style scoped>
.json-preview {
  background: #424242;
  color: #e0e0e0;
  padding: 12px;
  border-radius: 4px;
  font-size: 13px;
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
